/**
 * Admin bulk-create screen.
 *
 * Admin adaptation of customer-web `bulk-ship.screen.tsx`: the customer-web
 * version ships for the logged-in customer, here the admin first picks the
 * customer to ship FOR (CustomerSearch) and the selected `customerId` is fed
 * to `BulkCreateForm`. Changing the customer resets the form (key remount) so
 * customer-scoped reads (contacts / payment methods / terms) stay consistent.
 *
 * v1 import: an admin can also paste a v1 trip's JSON (Import from v1) to
 * prefill the review phase from an existing v1 trip — payload-based, no
 * v1-prod connection. See `./v1-prefill`.
 */
import { useRef, useState } from "react";
import { Upload, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CustomerSearch } from "@/features/customers/customer-search";
import { BulkCreateForm, type BulkCreateFormHandle } from "./bulk-create-form";
import { mapV1TripToRows, parseV1TripJson } from "./v1-prefill";

export function BulkCreateScreen() {
  const [customerId, setCustomerId] = useState<string | null>(null);
  const formRef = useRef<BulkCreateFormHandle>(null);

  return (
    <div className="container max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Bulk Create Orders
        </h1>
        <p className="text-muted-foreground">
          Upload a spreadsheet or enter rows manually to create multiple orders
          for a customer through the quote and payment flow.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Customer
          </CardTitle>
          <CardDescription>
            Select the customer these orders are created for. Their addresses,
            contacts, payment methods, and terms are used for the flow.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <CustomerSearch
            value={customerId}
            onValueChange={setCustomerId}
            placeholder="Select a customer..."
            className="max-w-md"
          />
          <ImportFromV1Dialog
            disabled={!customerId}
            onImport={(text) => {
              const trip = parseV1TripJson(text);
              const rows = mapV1TripToRows(trip);
              if (rows.length === 0) {
                throw new Error("The trip had no orders to import.");
              }
              formRef.current?.prefillRows(rows);
              return rows.length;
            }}
          />
        </CardContent>
      </Card>

      {customerId ? (
        // Remount on customer change so per-customer scoped reads + form state
        // never leak across customers.
        <BulkCreateForm
          key={customerId}
          ref={formRef}
          customerId={customerId}
        />
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Select a customer to begin.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface ImportFromV1DialogProps {
  /** Disabled until a customer is selected (the form isn't mounted yet). */
  disabled: boolean;
  /**
   * Parse + map + prefill. Returns the imported row count on success; throws a
   * clear Error on parse/map failure (surfaced as a toast).
   */
  onImport: (text: string) => number;
}

/** "Import from v1" dialog: paste a v1 trip JSON → prefill the review phase. */
function ImportFromV1Dialog({ disabled, onImport }: ImportFromV1DialogProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const handleImport = () => {
    try {
      const count = onImport(text);
      toast.success(`Imported ${count} order(s) from v1 trip`);
      setText("");
      setOpen(false);
    } catch (error) {
      toast.error("Could not import v1 trip", {
        description:
          error instanceof Error ? error.message : "Unexpected error.",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <Upload className="size-4" />
          Import from v1
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import from v1 trip</DialogTitle>
          <DialogDescription>
            Paste a v1 trip's JSON payload. Each order's delivery becomes a row
            in the review phase below.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder='{ "id": "...", "orders": [ ... ] }'
          className="h-64 font-mono text-xs"
        />

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!text.trim()}>
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkCreateScreen;
